import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { firstValueFrom } from "rxjs";

/**
 * ReservationsApiService (Angular 18).
 *
 * Replaces apps/b2b-ui/common/services/api/reservations/reservation.service.js,
 * which was a `.provider()` (not `.service()`) configured via
 * `ReservationApiServiceProvider.setEndpoint('/v2/api/reservations')` in the
 * legacy `api.service.js` config block. The endpoint is hardcoded here.
 *
 * Important contract difference vs the rest of `ApiService.*`: the legacy
 * service used `_promiseWrapper` to **unwrap** `response.data` and resolve
 * with the body directly. The single consumer
 * (`apps/b2b-ui/common/components/reservation-input/reservation-input.ts`)
 * relies on receiving `{ success, payload, error }` directly, not the
 * standard `{ data: { success, payload, error } }` envelope used by every
 * other migrated service. We preserve that behaviour by returning
 * `Promise<T>` instead of `Promise<AjsResponse<T>>` from this service.
 *
 * The legacy 300ms debounce on `update()` (via `UtilsService.debounce`) is
 * dropped here: the only call site for `update()` lives inside
 * `reserveOrUpdateTicket()` (reservation-input.ts:163), which itself is
 * currently disabled (the calling line is commented out as `// disable
 * feature ENG-1199`). If `update()` becomes live again, reintroduce the
 * debouncing on the consumer side rather than baking it into the API client.
 */
const RESERVATIONS_BASE_V2 = "/v2/api/reservations";

interface ReservationItem {
  quantity: number;
  eventId?: string;
}

interface Reservation {
  id?: string;
  expirationTimestamp?: string;
  reservationItems?: ReservationItem[];
}

@Injectable({ providedIn: "root" })
export class ReservationsApiService {
  private readonly http = inject(HttpClient);

  detail(id: string): Promise<unknown> {
    return firstValueFrom(
      this.http.get<unknown>(`${RESERVATIONS_BASE_V2}/${id}`),
    );
  }

  create(reservationItems: ReservationItem[]): Promise<unknown> {
    return firstValueFrom(
      this.http.post<unknown>(RESERVATIONS_BASE_V2, { reservationItems }),
    );
  }

  update(data: Reservation): Promise<unknown> {
    return firstValueFrom(this.http.put<unknown>(RESERVATIONS_BASE_V2, data));
  }

  cancel(id: string): Promise<unknown> {
    return firstValueFrom(
      this.http.delete<unknown>(`${RESERVATIONS_BASE_V2}/${id}`),
    );
  }
}
